import type { Environment } from '../../data/Environment';
import type { ImageObject } from '../../data/image/ImageObject';

/**
 * Which of a fused pair a view draws. The pair lives in one Environment, so
 * two views of it can show different sides of the same fusion: the primary
 * beside its registered secondary, rather than the two blended into one.
 * Every view draws the blend unless told otherwise.
 */
export enum FusedImageDisplay {
    Blended = 'blended',
    PrimaryOnly = 'primary-only',
    SecondaryOnly = 'secondary-only',
}

/** What one view hands the image renderer: the two slots and their blend. */
export interface FusedImageSlots {
    readonly primaryImage: ImageObject | undefined;
    readonly secondaryImage: ImageObject | undefined;
    readonly alpha: number;
    /**
     * Whether the Environment's own view mode applies. False means plain
     * alpha-over: checkerboard interleaves the pair, so with one slot showing
     * it would punch holes in the only image on screen.
     */
    readonly showsBlend: boolean;
    /**
     * Which of the Environment's colour masks the primary slot takes. The
     * secondary drawn alone keeps the secondary's mask, so it looks as it
     * does in the blend.
     */
    readonly primarySlotMask: 'primary' | 'secondary';
}

/**
 * The image a view of a fused pair draws on its own, or the primary for a
 * blended view. Undefined when the side asked for is not loaded, so a
 * secondary-only view of an unfused Environment draws nothing rather than
 * the primary.
 */
export function displayedImage(
    display: FusedImageDisplay,
    environment: Pick<Environment, 'image' | 'secondaryImage'>
): ImageObject | undefined {
    return display === FusedImageDisplay.SecondaryOnly
        ? environment.secondaryImage
        : environment.image;
}

/**
 * Resolves what a view draws of a fused pair.
 *
 * `Blended` keeps the Environment's own blend. Either single-side display
 * takes the plain alpha-over path with one image in the primary slot and
 * the secondary slot empty. The secondary keeps its own registered grid, so
 * drawn alone it still lands where the blend placed it, and the renderer
 * never paints the primary beneath it: a PET secondary over a CT would
 * otherwise show the CT wherever the PET has no data.
 */
export function resolveFusedImageSlots(
    display: FusedImageDisplay,
    environment: Pick<Environment, 'image' | 'secondaryImage' | 'alpha'>
): FusedImageSlots {
    if (display === FusedImageDisplay.Blended) {
        return {
            primaryImage: environment.image,
            secondaryImage: environment.secondaryImage,
            alpha: environment.alpha,
            showsBlend: true,
            primarySlotMask: 'primary',
        };
    }
    return {
        primaryImage: displayedImage(display, environment),
        secondaryImage: undefined,
        alpha: 1,
        showsBlend: false,
        primarySlotMask:
            display === FusedImageDisplay.SecondaryOnly
                ? 'secondary'
                : 'primary',
    };
}

/**
 * The series a window/level drag on a view changes: the image the view
 * draws alone, or the Environment's own windowing target for the blend.
 */
export function windowingSeriesUidFor(
    display: FusedImageDisplay,
    environment: Pick<
        Environment,
        'image' | 'secondaryImage' | 'activeWindowingSeriesUid'
    >
): string | undefined {
    return display === FusedImageDisplay.Blended
        ? environment.activeWindowingSeriesUid
        : displayedImage(display, environment)?.parent.seriesUid;
}
